import { ReactElement, ReactNode, useEffect } from 'react'
import { cloneDeep } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { validate } from '../middleware'

interface Props {
  children: ReactNode
}

interface FloorplanInitialState {
  /** Per-storey background image visibility in 2D floorplan view (default: visible). */
  storeyImageVisibilityIn2DByStorey: Record<string, boolean>
  /** Per-storey background image visibility in 3D view (default: hidden). */
  storeyImageVisibilityIn3DByStorey: Record<string, boolean>

  hiddenElementIdGroups: Record<string, string[]>

  elementTypeToSelectableGroups: Record<string, Partial<Record<ElementTypes, boolean>>>
}

interface FloorplanStoreType extends FloorplanInitialState {
  toggleStoreyImageVisibilityIn2D: (storey: string) => void
  toggleStoreyImageVisibilityIn3D: (storey: string) => void
  setStoreyImageVisibilityIn2DByStorey: (visibility: Record<string, boolean>) => void

  addHiddenFloorplanElementIdGroup: (idGroup: string, ids: string[]) => void
  removeHiddenFloorplanElementIdGroup: (idGroup: string) => void

  addElementTypeToSelectableGroup: (
    idGroup: string,
    elementTypeToSelectable: Partial<Record<ElementTypes, boolean>>,
  ) => void
  removeElementTypeToSelectableGroup: (idGroup: string) => void

  clear: () => void
}

const initialState: FloorplanInitialState = {
  storeyImageVisibilityIn2DByStorey: {},
  storeyImageVisibilityIn3DByStorey: {},

  hiddenElementIdGroups: {},

  elementTypeToSelectableGroups: {},
}

const createStore = () =>
  create<FloorplanStoreType>(
    validate(
      combine(cloneDeep(initialState), set => ({
        clear: () => set(cloneDeep(initialState)),

        toggleStoreyImageVisibilityIn2D: (storey: string) =>
          set(state => {
            const currentVisibility = state.storeyImageVisibilityIn2DByStorey[storey] ?? true

            return {
              storeyImageVisibilityIn2DByStorey: {
                ...state.storeyImageVisibilityIn2DByStorey,
                [storey]: !currentVisibility,
              },
            }
          }),

        toggleStoreyImageVisibilityIn3D: (storey: string) =>
          set(state => {
            const currentVisibility = state.storeyImageVisibilityIn3DByStorey[storey] ?? false

            return {
              storeyImageVisibilityIn3DByStorey: {
                ...state.storeyImageVisibilityIn3DByStorey,
                [storey]: !currentVisibility,
              },
            }
          }),

        setStoreyImageVisibilityIn2DByStorey: (visibility: Record<string, boolean>) =>
          set({ storeyImageVisibilityIn2DByStorey: visibility }),

        addHiddenFloorplanElementIdGroup: (idGroup: string, ids: string[]) =>
          set(state => ({
            hiddenElementIdGroups: {
              ...state.hiddenElementIdGroups,
              [idGroup]: ids,
            },
          })),
        removeHiddenFloorplanElementIdGroup: (idGroup: string) =>
          set(state => {
            const next = { ...state.hiddenElementIdGroups }
            delete next[idGroup]
            return { hiddenElementIdGroups: next }
          }),

        addElementTypeToSelectableGroup: (
          idGroup: string,
          elementTypeToSelectable: Partial<Record<ElementTypes, boolean>>,
        ) =>
          set(state => ({
            elementTypeToSelectableGroups: {
              ...state.elementTypeToSelectableGroups,
              [idGroup]: elementTypeToSelectable,
            },
          })),
        removeElementTypeToSelectableGroup: (idGroup: string) =>
          set(state => {
            const next = { ...state.elementTypeToSelectableGroups }
            delete next[idGroup]
            return { elementTypeToSelectableGroups: next }
          }),
      })),
    ),
  )

const useFloorplanStore = createStore()

const FloorplanStoreProvider = ({ children }: Props): ReactElement => {
  const clear = useFloorplanStore(state => state.clear)

  useEffect(() => {
    return () => clear()
  }, [])

  return <>{children}</>
}

export { FloorplanStoreProvider, useFloorplanStore }
